import React, { FC, ReactNode, useState } from 'react';
import { Tooltip } from 'src/common/components/Tooltip';
import { TooltipContent } from 'src/common/components/Tooltip/TooltipContent';
import LightbulbIcon from 'src/assets/icons/LightbulbIcon';
import { ClickAwayListener } from '@material-ui/core';
import styled, { css } from 'styled-components';
import { CleanButton } from 'src/common/components/Button';
import { colors } from 'src/config/colors';
import { below, media, widthBreakpoints } from 'src/config/breakpoints';
import { darken } from 'polished';
import { MediaQuery } from 'src/common/migration/MediaQuery';
import { GatsbyLink } from 'src/common/migration/GatsbyLink';

const Container = styled.div`
  grid-area: tooltip;
`;

const TooltipButton = styled(CleanButton)`
  background: ${colors.grey05};
  border-radius: 100%;
  font-size: 0.625rem;
  line-height: 0.875rem;
  padding: 0.3125rem 0.375rem;

  ${media.w.min.px768(css`
    border-radius: 0.375rem;
    padding: 0.25rem 0.625rem;
  `)};

  &:focus,
  &:hover {
    background: ${darken(0.1, colors.grey05)};
  }

  svg {
    ${media.w.min.px768(css`
      margin-right: 0.375rem;
    `)};
  }
`;

const TooltipButtonText = styled.span`
  ${media.w.max.px768(css`
    display: none;
  `)};
`;

const TooltipContainer = styled.span`
  grid-area: tooltip;

  ${media.w.min.px768(css`
    border-radius: 0.375rem;
    padding: 0.25rem 0.625rem;
  `)};
`;

export interface UserCoursesSectionTooltipProps {
  kind: 'internal' | 'external';
  title: ReactNode;
  to: string;
}

export const UserCoursesSectionTooltip: FC<UserCoursesSectionTooltipProps> = ({
  children,
  kind,
  title,
  to,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <Container>
      <MediaQuery minWidth={widthBreakpoints.px768}>
        {kind === 'internal' && (
          <TooltipButton as={GatsbyLink} to={to}>
            <LightbulbIcon size={12} />
            <TooltipButtonText>{title}</TooltipButtonText>
          </TooltipButton>
        )}

        {kind === 'external' && (
          <TooltipButton as="a" href={to} target="_blank">
            <LightbulbIcon size={12} />
            <TooltipButtonText>{title}</TooltipButtonText>
          </TooltipButton>
        )}
      </MediaQuery>

      <MediaQuery maxWidth={below(widthBreakpoints.px768)}>
        <ClickAwayListener onClickAway={() => setShowTooltip(false)}>
          <TooltipContainer>
            <Tooltip
              arrow
              disableFocusListener
              disableHoverListener
              disableTouchListener
              open={showTooltip}
              placement="top-start"
              title={
                <TooltipContent onClose={() => setShowTooltip(false)}>{children}</TooltipContent>
              }
            >
              <TooltipButton onClick={() => setShowTooltip(true)}>
                <LightbulbIcon size={12} />
              </TooltipButton>
            </Tooltip>
          </TooltipContainer>
        </ClickAwayListener>
      </MediaQuery>
    </Container>
  );
};
